import React, { useState, useEffect } from 'react';
import FormSlider from './FormSlider';
import api from '../services/api';
import '../styles/DrawerForm.css';
import '../styles/Buttons.css';

interface PackageFormSliderProps {
  workspaceId: string;
  packageId?: string;
  projectId?: string;
  onSuccess: () => void;
  onCancel: () => void;
  onLoadingChange?: (loading: boolean) => void;
}

interface Project {
  _id: string;
  name: string;
}

interface ColorCollection {
  _id: string;
  name: string;
  projectId: string;
}

interface PackageLocation {
  _id: string;
  name: string;
  order: number;
}

interface Phase {
  _id: string;
  name: string;
}

interface PackageFormData {
  order: number;
  projectId: string;
  name: string;
  caption: string;
  colorCollectionId: string;
  location: string;
  phase: string;
  isActive: boolean;
}

const PackageFormSlider: React.FC<PackageFormSliderProps> = ({
  workspaceId,
  packageId,
  projectId,
  onSuccess,
  onCancel,
  onLoadingChange
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [colorCollections, setColorCollections] = useState<ColorCollection[]>([]);
  const [locations, setLocations] = useState<PackageLocation[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [nextOrder, setNextOrder] = useState<number>(0);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);

  const initialFormData: PackageFormData = {
    order: nextOrder,
    projectId: projectId || '',
    name: '',
    caption: '',
    colorCollectionId: '',
    location: '',
    phase: '',
    isActive: false
  };

  useEffect(() => {
    fetchProjects();
  }, [workspaceId]);

  useEffect(() => {
    if (projectId && !packageId) {
      fetchColorCollections(projectId);
      fetchLocations(projectId);
      fetchNextOrder(projectId);
    }
  }, [projectId, packageId, workspaceId]);

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      setProjects(response.data.projects);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchColorCollections = async (selectedProjectId: string) => {
    if (!selectedProjectId) {
      setColorCollections([]);
      return;
    }

    try {
      setLoadingCollections(true);
      const response = await api.get(`/workspaces/${workspaceId}/pkg-color-collections`, {

        params: {
          projectId: selectedProjectId,
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      setColorCollections(response.data.colorCollections || []);
    } catch (error) {
      console.error('Error fetching color collections:', error);
      setColorCollections([]);
    } finally {
      setLoadingCollections(false);
    }
  };

  const fetchLocations = async (selectedProjectId: string) => {
    if (!selectedProjectId) {
      setLocations([]);
      return;
    }

    try {
      setLoadingLocations(true);
      const response = await api.get(`/workspaces/${workspaceId}/pkglocations`, {
        params: {
          projectId: selectedProjectId,
          limit: 1000,
          sortBy: 'order',
          sortOrder: 'asc'
        }
      });
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  };

  const fetchNextOrder = async (selectedProjectId: string, updateCallback?: (order: number) => void) => {
    try {
      setIsLoadingOrder(true);
      const response = await api.get(`/workspaces/${workspaceId}/pkg-packages`, {
        params: { projectId: selectedProjectId, limit: 1, sortBy: 'order', sortOrder: 'desc' }
      });

      const packages = response.data.packages;
      const nextOrderValue = packages && packages.length > 0 ? packages[0].order + 1 : 0;

      setNextOrder(nextOrderValue);
      if (updateCallback) {
        updateCallback(nextOrderValue);
      }
    } catch (error) {
      console.error('Error fetching next order:', error);
      setNextOrder(0);
      if (updateCallback) {
        updateCallback(0);
      }
    } finally {
      setIsLoadingOrder(false);
    }
  };

  // Static phase list - no need to fetch
  const phases = [
    { _id: 'phase-1', name: 'One' },
    { _id: 'phase-2', name: 'Two' },
    { _id: 'phase-3', name: 'Three' },
    { _id: 'phase-4', name: 'Four' },
    { _id: 'phase-5', name: 'Five' }
  ];

  const validateForm = (data: PackageFormData): string | null => {
    if (!data.projectId) {
      return 'Project is required';
    }
    if (!data.name.trim()) {
      return 'Name is required';
    }
    if (!data.colorCollectionId) {
      return 'Color Collection is required';
    }
    if (!data.location.trim()) {
      return 'Location is required';
    }
    if (!data.phase) {
      return 'Phase is required';
    }
    return null;
  };

  const transformFetchData = (response: any): PackageFormData => {
    const pkg = response.package;

    // Load color collections and locations when editing
    if (pkg.projectId) {
      fetchColorCollections(pkg.projectId);
      fetchLocations(pkg.projectId);
    }

    return {
      order: pkg.order || 1,
      projectId: pkg.projectId || '',
      name: pkg.name || '',
      caption: pkg.caption || '',
      colorCollectionId: pkg.collections?.[0]?.collectionId || '',
      location: pkg.location || '',
      phase: pkg.phase || '',
      isActive: pkg.isActive ?? false
    };
  };

  const transformSubmitData = (data: PackageFormData) => {
    const submitData: any = {
      order: data.order,
      projectId: data.projectId,
      name: data.name.trim(),
      caption: data.caption.trim() || undefined,
      location: data.location.trim(),
      phase: data.phase,
      isActive: data.isActive
    };

    // Add color collection if selected
    if (data.colorCollectionId) {
      const selectedCollection = colorCollections.find(c => c._id === data.colorCollectionId);
      submitData.collections = [{
        collectionId: data.colorCollectionId,
        name: selectedCollection?.name || ''
      }];
    }

    return submitData;
  };

  const handleProjectChange = (formData: PackageFormData, handleFieldChange: (field: keyof PackageFormData, value: any) => void) => {
    return (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newProjectId = e.target.value;
      handleFieldChange('projectId', newProjectId);
      handleFieldChange('colorCollectionId', '');
      handleFieldChange('location', '');
      handleFieldChange('phase', '');

      if (newProjectId) {
        fetchColorCollections(newProjectId);
        fetchLocations(newProjectId);
        // Fetch and set next order only for new packages
        if (!packageId) {
          fetchNextOrder(newProjectId, (order) => {
            handleFieldChange('order', order);
          });
        }
      } else {
        setColorCollections([]);
        setLocations([]);
        setNextOrder(0);
      }
    };
  };

  // Don't render the form until projects and order are loaded for new packages
  if (!packageId && (loadingProjects || (projectId && isLoadingOrder))) {
    return (
      <div className="builders-loading" style={{ padding: '64px 0' }}>
        <div className="builders-loading-spinner" />
      </div>
    );
  }

  return (
    <FormSlider
      key={`package-form-${nextOrder}`}
      workspaceId={workspaceId}
      itemId={packageId}
      onSuccess={onSuccess}
      onCancel={onCancel}
      apiEndpoint={`/workspaces/${workspaceId}/pkg-packages`}
      initialFormData={initialFormData}
      validateForm={validateForm}
      transformFetchData={transformFetchData}
      transformSubmitData={transformSubmitData}
      formId="package-form"
      onLoadingChange={onLoadingChange}
    >
      {({ formData, handleInputChange, handleFieldChange, loading }) => (
        <>
          <div className="form-group">
            <label htmlFor="projectId" className="form-label">
              Project
            </label>
            {loadingProjects ? (
              <div className="builders-loading" style={{ padding: '20px 0', textAlign: 'center' }}>
                <div className="builders-loading-spinner" />
                <p style={{ marginTop: '10px', fontSize: '13px', color: '#6B7280' }}>Loading projects...</p>
              </div>
            ) : (
              <>
                <select
                  id="projectId"
                  name="projectId"
                  className="form-input"
                  value={formData.projectId}
                  onChange={handleProjectChange(formData, handleFieldChange)}
                  required
                  disabled={loading || !!packageId}
                >
                  <option value="">Select Project</option>
                  {projects.map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.name}
                    </option>
                  ))}
                </select>
                {packageId && (
                  <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#6B7280' }}>
                    Project cannot be changed after creation
                  </small>
                )}
              </>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="order" className="form-label">
              Order
            </label>
            <input
              type="number"
              id="order"
              name="order"
              className="form-input"
              value={formData.order}
              onChange={handleInputChange}
              required
              disabled={true}
              min="0"
            />
            <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#6B7280' }}>
              Auto-generated based on existing packages
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="name" className="form-label">
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              className="form-input"
              value={formData.name}
              onChange={handleInputChange}
              required
              disabled={loading}
              placeholder="Enter package name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="caption" className="form-label">
              Caption
            </label>
            <input
              type="text"
              id="caption"
              name="caption"
              className="form-input"
              value={formData.caption}
              onChange={handleInputChange}
              disabled={loading}
              placeholder="Enter caption"
            />
          </div>

          <div className="form-group">
            <label htmlFor="colorCollectionId" className="form-label">
              Color Collection
            </label>
            {loadingCollections ? (
              <div className="builders-loading" style={{ padding: '20px 0', textAlign: 'center' }}>
                <div className="builders-loading-spinner" />
                <p style={{ marginTop: '10px', fontSize: '13px', color: '#6B7280' }}>Loading collections...</p>
              </div>
            ) : (
              <select
                id="colorCollectionId"
                name="colorCollectionId"
                className="form-input"
                value={formData.colorCollectionId}
                onChange={handleInputChange}
                required
                disabled={loading || !formData.projectId}
              >
                <option value="">Select Collection</option>
                {colorCollections.map((collection) => (
                  <option key={collection._id} value={collection._id}>
                    {collection.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="location" className="form-label">
              Locations
            </label>
            {loadingLocations ? (
              <select className="form-input" disabled>
                <option>Loading locations...</option>
              </select>
            ) : (
              <select
                id="location"
                name="location"
                className="form-input"
                value={formData.location}
                onChange={handleInputChange}
                required
                disabled={loading || !formData.projectId}
              >
                <option value="">Select Location</option>
                {locations.map((location) => (
                  <option key={location._id} value={location.name}>
                    {location.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="phase" className="form-label">
              Phase
            </label>
            <select
              id="phase"
              name="phase"
              className="form-input"
              value={formData.phase}
              onChange={handleInputChange}
              required
              disabled={loading}
            >
              <option value="">Select Phase</option>
              {phases.map((phase) => (
                <option key={phase._id} value={phase.name}>
                  {phase.name}
                </option>
              ))}
            </select>
          </div>

          <div className="drawer-form-checkbox-group">
            <label className="drawer-form-checkbox-label" htmlFor="isActive">
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={formData.isActive}
                onChange={(e) => handleFieldChange('isActive', e.target.checked)}
                disabled={loading}
              />
              <span>Active</span>
            </label>
          </div>
        </>
      )}
    </FormSlider>
  );
};

export default PackageFormSlider;
